import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Plus, X, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray50: '#F9FAFB', gray100: '#F3F4F6',
  gray500: '#6B7280', gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info: '#1E40AF', infoBg: '#DBEAFE',
  gold: '#B45309', goldBg: '#FEF9C3',
};

const BN = {
  title: 'Pengiktirafan & Anugerah', subtitle: 'rekod', back: '← Laman Utama',
  addAward: '+ Rekod Anugerah', tabs: ['Anugerah', 'Laporan'] as const,
  search: 'Cari', allType: 'Semua Jenis', allStatus: 'Semua Status',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Anugerah', emptyBody: 'Tiada rekod anugerah dijumpai.',
  addTitle: 'Rekod Anugerah Baharu', educatorId: 'ID Pendidik *', awardType: 'Jenis Anugerah *',
  titleEn: 'Tajuk (EN) *', titleMs: 'Tajuk (MS)', issuingBody: 'Badan Penganugerah *',
  awardDate: 'Tarikh Anugerah *', description: 'Penerangan', certificateUrl: 'URL Sijil',
  save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  verify: 'Sahkan', reject: 'Tolak',
  totalAwards: 'Jumlah Anugerah', verified: 'Disahkan', pending: 'Menunggu',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
};
const EN = {
  title: 'Educator Recognition & Awards', subtitle: 'records', back: '← Home',
  addAward: '+ Record Award', tabs: ['Awards', 'Reports'] as const,
  search: 'Search', allType: 'All Types', allStatus: 'All Status',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No Awards', emptyBody: 'No award records found.',
  addTitle: 'Record New Award', educatorId: 'Educator ID *', awardType: 'Award Type *',
  titleEn: 'Title (EN) *', titleMs: 'Title (MS)', issuingBody: 'Issuing Body *',
  awardDate: 'Award Date *', description: 'Description', certificateUrl: 'Certificate URL',
  save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  verify: 'Verify', reject: 'Reject',
  totalAwards: 'Total Awards', verified: 'Verified', pending: 'Pending',
  retry: 'Retry', errLoad: 'Failed to load.',
};

const AWARD_TYPES = ['NATIONAL','MINISTRY','DISTRICT','SCHOOL','PROFESSIONAL','COMMUNITY','ACADEMIC','SPORTS'];
const TYPE_CFG: Record<string, { ms: string; en: string; color: string; bg: string }> = {
  NATIONAL:     { ms: 'Kebangsaan',   en: 'National',     color: '#B45309', bg: '#FEF9C3' },
  MINISTRY:     { ms: 'Kementerian',  en: 'Ministry',     color: '#1E40AF', bg: '#DBEAFE' },
  DISTRICT:     { ms: 'Daerah',       en: 'District',     color: '#0F766E', bg: '#CCFBF1' },
  SCHOOL:       { ms: 'Sekolah',      en: 'School',       color: '#166534', bg: '#DCFCE7' },
  PROFESSIONAL: { ms: 'Profesional',  en: 'Professional', color: '#6D28D9', bg: '#EDE9FE' },
  COMMUNITY:    { ms: 'Komuniti',     en: 'Community',    color: '#BE185D', bg: '#FCE7F3' },
  ACADEMIC:     { ms: 'Akademik',     en: 'Academic',     color: '#0369A1', bg: '#E0F2FE' },
  SPORTS:       { ms: 'Sukan',        en: 'Sports',       color: '#EA580C', bg: '#FFF7ED' },
};
const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  PENDING:  { label: 'Menunggu',   color: '#92400E', bg: '#FEF3C7' },
  VERIFIED: { label: 'Disahkan',  color: '#166534', bg: '#DCFCE7' },
  REJECTED: { label: 'Ditolak',   color: '#991B1B', bg: '#FEE2E2' },
};

interface Props { user: AuthUser }
interface Award { id: string; educator_id: string; award_type: string; title_en: string; title_ms: string | null; issuing_body: string; award_date: string; description: string | null; certificate_url: string | null; status: string; verifier_notes: string | null; full_name_en: string | null; full_name_ms: string | null; service_number: string; school_name: string | null }
interface PageResult { data: Award[]; total: number; page: number }

const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';
const LIMIT = 20;

export function AwardsPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit   = hasRole(user, 'super_admin', 'admin', 'principal', 'clerk');
  const canVerify = hasRole(user, 'super_admin', 'admin', 'principal');
  const schoolId  = schoolIdFromUser(user) ?? '';

  const [tab,    setTab]    = useState<'awards' | 'reports'>('awards');
  const [awards, setAwards] = useState<Award[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [form, setForm] = useState({
    educatorId: '', awardType: 'SCHOOL', titleEn: '', titleMs: '', issuingBody: '',
    awardDate: '', description: '', certificateUrl: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ schoolId, page: String(page) });
      if (typeFilter)   p.set('awardType', typeFilter);
      if (statusFilter) p.set('status', statusFilter);
      const res = await apiGet<PageResult>(`/api/awards?${p}`);
      setAwards(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, typeFilter, statusFilter, UI.errLoad]);

  useEffect(() => { if (tab === 'awards') load(); }, [tab, load]);

  const submitAward = async () => {
    if (!form.educatorId.trim() || !form.titleEn.trim() || !form.issuingBody.trim() || !form.awardDate) return;
    setSaving(true);
    try {
      await apiPost('/api/awards', {
        educatorId: form.educatorId, awardType: form.awardType,
        titleEn: form.titleEn, titleMs: form.titleMs || undefined,
        issuingBody: form.issuingBody, awardDate: form.awardDate,
        description: form.description || undefined, certificateUrl: form.certificateUrl || undefined,
      });
      setShowAdd(false);
      setForm({ educatorId: '', awardType: 'SCHOOL', titleEn: '', titleMs: '', issuingBody: '', awardDate: '', description: '', certificateUrl: '' });
      setToast({ message: lang === 'en' ? 'Award recorded.' : 'Anugerah direkodkan.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiPatch(`/api/awards/${id}/status`, { status });
      setToast({ message: lang === 'en' ? 'Status updated.' : 'Status dikemas kini.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900, backgroundColor: C.white, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  const summary = { total: awards.length, verified: awards.filter(a => a.status === 'VERIFIED').length, pending: awards.filter(a => a.status === 'PENDING').length };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.goldBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Trophy size={20} color={C.gold} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && tab === 'awards' && (
            <button onClick={() => setShowAdd(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>
              {UI.addAward}
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: UI.totalAwards, value: total, color: C.gold, bg: C.goldBg },
          { label: UI.verified, value: summary.verified, color: C.success, bg: C.successBg },
          { label: UI.pending, value: summary.pending, color: C.warning, bg: C.warningBg },
        ].map(card => (
          <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: card.color }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        {(['awards', 'reports'] as const).map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: tab === t ? C.black : C.gray500, borderBottom: tab === t ? `2px solid ${C.yellow}` : '2px solid transparent', marginBottom: '-1px' }}>
            {lang === 'en' ? EN.tabs[i] : BN.tabs[i]}
          </button>
        ))}
      </div>

      {tab === 'awards' && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {[
              { val: typeFilter, set: setTypeFilter, opts: AWARD_TYPES.map(t => ({ v: t, l: lang === 'en' ? TYPE_CFG[t]?.en ?? t : TYPE_CFG[t]?.ms ?? t })), placeholder: UI.allType },
              { val: statusFilter, set: setStatusFilter, opts: Object.keys(STATUS_CFG).map(s => ({ v: s, l: STATUS_CFG[s]!.label })), placeholder: UI.allStatus },
            ].map(({ val, set, opts, placeholder }) => (
              <select key={placeholder} value={val} onChange={e => { set(e.target.value); setPage(1); }} style={{ ...inputStyle, width: '180px' }}>
                <option value="">{placeholder}</option>
                {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ))}
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
          {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error} <button onClick={load} style={{ marginLeft: '8px', color: C.error, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{UI.retry}</button></div>}

          {!loading && awards.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <Trophy size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
            </div>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {awards.map(a => {
              const tc = TYPE_CFG[a.award_type];
              const sc = STATUS_CFG[a.status];
              return (
                <div key={a.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Star size={16} color={C.gold} fill={C.gold} />
                        <span style={{ fontWeight: 700, fontSize: '15px', color: C.gray900 }}>{lang === 'en' ? a.title_en : (a.title_ms ?? a.title_en)}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: C.gray700 }}>
                        {lang === 'en' ? (a.full_name_en ?? a.service_number) : (a.full_name_ms ?? a.full_name_en ?? a.service_number)}
                        {a.school_name ? ` · ${a.school_name}` : ''}
                      </div>
                      <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px' }}>
                        {a.issuing_body} · {new Date(a.award_date).toLocaleDateString('ms-BN')}
                      </div>
                      {a.description && <div style={{ fontSize: '12px', color: C.gray500, marginTop: '4px', fontStyle: 'italic' }}>{a.description}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, marginLeft: '12px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: tc?.bg, color: tc?.color }}>{lang === 'en' ? tc?.en : tc?.ms}</span>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: sc?.bg, color: sc?.color }}>{sc?.label}</span>
                    </div>
                  </div>
                  {canVerify && a.status === 'PENDING' && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button onClick={() => updateStatus(a.id, 'VERIFIED')} style={{ padding: '5px 14px', borderRadius: '6px', background: C.successBg, border: 'none', color: C.success, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>{UI.verify}</button>
                      <button onClick={() => updateStatus(a.id, 'REJECTED')} style={{ padding: '5px 14px', borderRadius: '6px', background: C.errorBg, border: 'none', color: C.error, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>{UI.reject}</button>
                    </div>
                  )}
                  {a.certificate_url && (
                    <a href={a.certificate_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: C.info, marginTop: '6px', display: 'inline-block' }}>
                      {lang === 'en' ? 'View Certificate' : 'Lihat Sijil'} ↗
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {total > LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
              <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
                <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'reports' && (
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>{lang === 'en' ? 'Awards by Type' : 'Anugerah Mengikut Jenis'}</h3>
            <div style={{ display: 'grid', gap: '8px' }}>
              {AWARD_TYPES.map(type => {
                const count = awards.filter(a => a.award_type === type).length;
                const pct = Math.round((count / Math.max(1, total)) * 100);
                const cfg = TYPE_CFG[type];
                return (
                  <div key={type}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: C.gray700 }}>{lang === 'en' ? cfg?.en : cfg?.ms}</span>
                      <span style={{ fontWeight: 700 }}>{count} ({pct}%)</span>
                    </div>
                    <div style={{ height: '6px', background: C.gray100, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: cfg?.color ?? C.gold, borderRadius: '3px', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Add Award Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '500px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addTitle}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { key: 'educatorId', label: UI.educatorId, type: 'text' },
                { key: 'titleEn', label: UI.titleEn, type: 'text' },
                { key: 'titleMs', label: UI.titleMs, type: 'text' },
                { key: 'issuingBody', label: UI.issuingBody, type: 'text' },
                { key: 'awardDate', label: UI.awardDate, type: 'date' },
                { key: 'certificateUrl', label: UI.certificateUrl, type: 'url' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.awardType}</label>
                <select value={form.awardType} onChange={e => setForm(p => ({ ...p, awardType: e.target.value }))} style={inputStyle}>
                  {AWARD_TYPES.map(t => <option key={t} value={t}>{lang === 'en' ? TYPE_CFG[t]?.en : TYPE_CFG[t]?.ms}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{UI.description}</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  style={{ ...inputStyle, height: '70px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submitAward} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
